import { useEffect, useState } from "react";
import { useAlert } from "../../../Hooks/useAlert";
import { useLanguage } from "../../../context/LanguageContext";
import salesLanguage from "../../../language/salesLanguage";
import { salesAPI } from "../../../services/api";
import Alert from "../../common/Alert";
import ConfirmDialog from "../../common/ConfirmDialog";
import BackButton from "./BackButton";

const TODAY = () => new Date().toISOString().split("T")[0];

function SalesPage() {
  const { language } = useLanguage();
  const text = salesLanguage[language];
  const { showAlert, showConfirm, alertConfig, confirmConfig } = useAlert();

  const [saleId, setSaleId] = useState(0);
  const [productName, setProductName] = useState("");
  const [amount, setAmount] = useState("");
  const [saleDate, setSaleDate] = useState(TODAY());
  const [saleEntries, setSaleEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSalesEntries();
  }, []);

  const loadSalesEntries = async () => {
    try {
      const response = await salesAPI.getSalesEntries();
      if (response.success) setSaleEntries(response.data || []);
    } catch (error) {
      console.error("Error loading sales entries:", error);
    }
  };

  const resetForm = () => {
    setSaleId(0);
    setProductName("");
    setAmount("");
    setSaleDate(TODAY());
  };

  const handleSave = async () => {
    if (!productName.trim() || !amount) {
      await showAlert({ type: "warning", title: text.validationError, message: text.salesValidation });
      return;
    }
    setLoading(true);
    try {
      const data = {
        salesId: saleId,
        productName: productName.trim(),
        amount: parseFloat(amount),
        entryDate: saleDate,
      };
      const response = await salesAPI.saveSalesEntry(data);
      if (response.success) {
        await showAlert({ type: "success", title: text.success, message: saleId === 0 ? text.savedSuccess : text.updatedSuccess });
        resetForm();
        await loadSalesEntries();
      } else {
        await showAlert({ type: "error", title: text.error, message: text.saveFailed });
      }
    } catch (error) {
      console.error("Error saving sale:", error);
      await showAlert({ type: "error", title: text.error, message: text.saveFailed });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setSaleId(entry.salesId);
    setProductName(entry.productName);
    setAmount(entry.amount.toString());
    setSaleDate(new Date(entry.entryDate).toISOString().split("T")[0]);
  };

  const handleDelete = async (entry) => {
    const confirmed = await showConfirm({
      type: "error",
      title: text.deleteConfirmTitle,
      message: text.deleteConfirmMsg,
      confirmText: text.delete,
      cancelText: text.deleteCancel,
    });
    if (!confirmed) return;
    try {
      const response = await salesAPI.deleteSalesEntry(entry.salesId);
      if (response.success) {
        await showAlert({ type: "success", title: text.success, message: text.deletedSuccess });
        await loadSalesEntries();
      } else {
        await showAlert({ type: "error", title: text.error, message: text.deleteFailed });
      }
    } catch (error) {
      console.error("Error deleting sale:", error);
      await showAlert({ type: "error", title: text.error, message: text.deleteFailed });
    }
  };

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <BackButton to="/settings" section="salesExpenses" />
        <h1 className="settings-page-title">Sales Master</h1>
      </header>

      <main className="settings-page-content">
        <div className="settings-form-card">
          <div className="settings-form-body">
            <div className="settings-form-group">
              <label className="settings-form-label">{text.saleProductName}</label>
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="settings-form-input"
                placeholder={text.saleProductName}
              />
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">{text.amount}</label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="settings-form-input"
              />
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">{text.date}</label>
              <input
                type="date"
                value={saleDate}
                max={TODAY()}
                onChange={(e) => setSaleDate(e.target.value)}
                className="settings-form-input"
              />
            </div>

            <div className="settings-form-actions">
              <button
                type="button"
                disabled={loading}
                onClick={handleSave}
                className="settings-form-btn"
              >
                <i className="fas fa-save" />
                <span>{loading ? text.saving : saleId === 0 ? text.save : text.update}</span>
              </button>
              {saleId !== 0 && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="settings-form-btn settings-form-btn-secondary"
                >
                  <i className="fas fa-times" />
                  <span>{text.cancel}</span>
                </button>
              )}
            </div>

            {saleEntries.length > 0 && (
              <div className="settings-table-wrapper">
                <h3 className="settings-table-title">{text.recentSales}</h3>
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th className="settings-table-th settings-table-th-center">{text.colActions}</th>
                      <th className="settings-table-th">{text.colDate}</th>
                      <th className="settings-table-th">{text.colProduct}</th>
                      <th className="settings-table-th settings-table-th-right">{text.colAmount}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saleEntries.map((entry, index) => (
                      <tr key={entry.salesId} className={`settings-table-tr ${index % 2 === 1 ? "settings-table-tr-zebra" : ""}`}>
                        <td className="settings-table-td settings-table-td-center">
                          <div className="settings-table-actions">
                            <button type="button" onClick={() => handleEdit(entry)} className="settings-action-btn settings-action-btn-edit">
                              <i className="fas fa-pen" />
                            </button>
                            <button type="button" onClick={() => handleDelete(entry)} className="settings-action-btn settings-action-btn-delete">
                              <i className="fas fa-trash-alt" />
                            </button>
                          </div>
                        </td>
                        <td className="settings-table-td">{new Date(entry.entryDate).toLocaleDateString("en-IN")}</td>
                        <td className="settings-table-td">{entry.productName}</td>
                        <td className="settings-table-td settings-table-td-right settings-table-td-bold">₹{entry.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {alertConfig && <Alert {...alertConfig} />}
      {confirmConfig && <ConfirmDialog {...confirmConfig} />}
    </div>
  );
}

export default SalesPage;
